import React, { useState, useRef, useEffect, useId, useImperativeHandle } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const SearchableSelect = React.forwardRef(function SearchableSelect(
  {
    label,
    /** Optional node shown to the right of the label (e.g. status badge). */
    labelAside,
    value,
    onChange,
    options = [],
    placeholder = 'Select an option',
    error,
    disabled = false,
    required = false,
    /** Label and control on one row (e.g. truck picker in weighing modals). */
    inlineLabel = false,
    dropdownFooter,
    className = '',
    ...props
  },
  ref
) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const comboboxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();

  useImperativeHandle(
    ref,
    () => ({
      focus: ({ open = false, preventScroll = true } = {}) => {
        if (disabled) return;
        const el = comboboxRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        el.focus({ preventScroll });
        if (open) {
          setIsOpen(true);
        }
      }
    }),
    [disabled]
  );

  const optionMatchesSearch = (option, term) => {
    const raw = (term || '').trim();
    if (!raw) return true;
    const needle = raw.toLowerCase();
    const haystack = `${option.searchText || ''} ${option.label || ''}`.toLowerCase();
    if (haystack.includes(needle)) return true;
    const digitsNeedle = raw.replace(/\D/g, '');
    if (digitsNeedle.length >= 4) {
      const digitsHay = haystack.replace(/\D/g, '');
      if (digitsHay.includes(digitsNeedle)) return true;
    }
    return false;
  };

  // Filter options: label + optional searchText (e.g. phone); digit-only match for telephone search
  const filteredOptions = options.filter((option) => optionMatchesSearch(option, searchTerm));

  // Get selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (option) => {
    const syntheticEvent = {
      target: {
        name: props.name || '',
        value: option.value
      }
    };
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    const syntheticEvent = {
      target: {
        name: props.name || '',
        value: ''
      }
    };
    onChange(syntheticEvent);
    setSearchTerm('');
  };

  const selectClasses = `
    input-field
    cursor-pointer
    ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
  `.trim().replace(/\s+/g, ' ');

  const labelEl = label ? (
    <div className={inlineLabel ? 'min-w-0' : 'mb-1'}>
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        <label
          className={`text-sm font-medium text-gray-700 ${inlineLabel ? 'min-w-0 sm:whitespace-nowrap' : ''}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {labelAside ? (
          <div className="shrink-0 max-w-[min(100%,20rem)] text-right">{labelAside}</div>
        ) : null}
      </div>
    </div>
  ) : null;

  const controlField = (
    <div className={`relative w-full ${inlineLabel ? 'min-w-0' : ''}`}>
      <div
        ref={comboboxRef}
        className={selectClasses}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
      >
        <div className="flex items-center justify-between">
          <span className={`min-w-0 truncate ${value ? 'text-gray-900' : 'text-gray-400'}`}>
            {displayValue || placeholder}
          </span>
          <div className="flex items-center space-x-1 shrink-0">
            {value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                tabIndex={-1}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            />
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 flex max-h-72 flex-col overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg">
          <div className="shrink-0 border-b border-gray-200 p-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                placeholder={t('common.search')}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div
            ref={listRef}
            className="min-h-0 flex-1 overflow-y-auto"
            id={listboxId}
            role="listbox"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`
                      px-3 py-2 text-sm cursor-pointer
                      ${value === option.value ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-900'}
                      ${highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'}
                    `}
                  role="option"
                  aria-selected={value === option.value}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                {t('common.noOptionsFound', { defaultValue: 'No options found' })}
              </div>
            )}
          </div>
          {dropdownFooter && (
            <div
              className="shrink-0 border-t border-gray-200 bg-white p-2"
              onMouseDown={(e) => {
                // Keep dropdown stable while interacting with footer action.
                e.preventDefault();
              }}
            >
              {typeof dropdownFooter === 'function'
                ? dropdownFooter({ closeDropdown: () => setIsOpen(false) })
                : dropdownFooter}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={className} ref={wrapperRef}>
      {inlineLabel ? (
        <div className="grid w-full grid-cols-1 gap-x-3 gap-y-1.5 items-center sm:grid-cols-[minmax(0,max-content)_minmax(0,1fr)]">
          {labelEl}
          {controlField}
        </div>
      ) : (
        <>
          {labelEl}
          {controlField}
        </>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
});

export default SearchableSelect;
